import { generalRef, specRef } from './cap-tmai-source-refs.js';
// ─── TMAI CAP framework — March 2022 ────────────────────────────────────────
// 1:1 transcription of `__docs/cap-framework-tmai-2022-03.json` (the validated
// seed). The const IS the canonical runtime config (typed, versioned, no
// Firestore reads); the JSON stays in __docs/ as documentary source. The
// contract test (`__tests__/cap-tmai-2022-03.spec.ts`) pins the version and
// the TCU invariants to detect drift between the two.
//
// ONE FIELD IS NOT IN THAT SEED: `sourceRef`. It points at the section of the
// TMAI source documents that states each requirement, so a figure can be given
// together with the rule behind it. It is metadata about where the datum comes
// from, never a datum — the values above stay 1:1 with the seed. Built by
// `cap-tmai-source-refs.ts`, and `__tests__/cap-tmai-source-refs.spec.ts`
// checks every one of them resolves to a heading that actually exists in
// `__docs/SEED-tmai-cap-framework-2022-03.md`.
export const TMAI_CAP_FRAMEWORK_2022_03 = {
    id: 'tmai-cap',
    name: 'TMAI Certified Academic Programs',
    sourceVersion: '2022-03',
    sourceDocs: [
        'TMAI-CAPs - General Instructions - March 2022',
        'TMAI-Biblical Studies Certificate CAP Specification - March 2022',
        'TMAI-DEP CAP Specification - March 2022',
    ],
    status: 'PENDING_VALIDATION_CURRENT_VERSION',
    languageNote: 'Los PLOs oficiales están en inglés y son autoritativos para el paquete que revisa TMAI. Las traducciones al español (es) son para la UI (fuente de UI = es). La propuesta CAP se somete en inglés.',
    tcuStandard: {
        undergraduateHoursPerTcu: [37, 45],
        graduateHoursPerTcu: [50, 60],
        note: '1 TCU = horas de dedicación del estudiante (clase + lectura + tareas + proyectos + exámenes). Posgrado usa 37–45 solo si el rigor es realmente de nivel avanzado; normalmente 50–60.',
        sourceRef: generalRef('creditUnits'),
    },
    alignmentLevels: ['I', 'T', 'P', 'E'],
    alignmentLevelLabels: {
        I: { es: 'Introducido', en: 'Introduced' },
        T: { es: 'Enseñado', en: 'Taught' },
        P: { es: 'Practicado', en: 'Practiced' },
        E: { es: 'Evaluado (signature assignment)', en: 'Evaluated (signature assignment)' },
    },
    levels: ['undergraduate', 'graduate', 'mixed'],
    auditRules: {
        requireEveryPloEvaluated: true,
        requireEveryCourseContributesToAtLeastOnePlo: true,
        totalTcuMustMeetMinimum: true,
        subjectAreaTcuMustMeetMinimum: true,
        prerequisiteCapMustBeRecognizedIfStacked: true,
    },
    caps: [
        {
            id: 'bsc',
            code: 'BSC',
            order: 1,
            name: { es: 'Certificado en Estudios Bíblicos', en: 'Biblical Studies Certificate' },
            prerequisiteCapId: null,
            isPrerequisiteFor: ['dep'],
            levelOptions: ['undergraduate', 'graduate', 'mixed'],
            totalMinTcu: { freeStanding: 21, additionalIfStacked: null },
            awards: {
                undergraduate: {
                    es: 'Certificado en Estudios Bíblicos',
                    en: 'Certificate in Biblical Studies',
                },
                graduate: {
                    es: 'Certificado (nivel posgrado) en Estudios Bíblicos',
                    en: 'Graduate-level Certificate in Biblical Studies',
                },
            },
            entranceRequirements: {
                undergraduate: 'Diploma de secundaria (según contexto local). Si no lo tiene, el TC debe explicar cómo asegura el mismo nivel de dominio.',
                graduate: 'Grado de bachillerato (según contexto local); el TC debe certificar su cumplimiento.',
            },
            instructionalLanguageRule: 'Si los cursos no se dictan en la lengua materna del estudiante, la propuesta debe describir cómo se evalúa la fluidez en el idioma de instrucción.',
            facultyRequirements: {
                undergraduate: 'Al menos maestría en un campo relacionado con estudios bíblicos.',
                graduate: 'Al menos M.Div.',
            },
            timeToCompleteMonthsTarget: 36,
            deliveryFormats: [
                'residential',
                'modular_onsite',
                'modular_blended',
                'online_independent',
                'online_with_sessions',
            ],
            subjectAreaRequirements: [
                {
                    code: 'OT_SURVEY',
                    name: { es: 'Panorama del Antiguo Testamento', en: 'Old Testament Survey' },
                    minTcu: 6,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('BSC', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'NT_SURVEY',
                    name: { es: 'Panorama del Nuevo Testamento', en: 'New Testament Survey' },
                    minTcu: 3,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('BSC', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'HERMENEUTICS',
                    name: { es: 'Hermenéutica', en: 'Hermeneutics' },
                    minTcu: 3,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('BSC', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'THEOLOGY',
                    name: { es: 'Teología Cristiana', en: 'Christian Theology' },
                    minTcu: 6,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('BSC', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'ELECTIVES',
                    name: { es: 'Electivos', en: 'Electives' },
                    minTcu: 3,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('BSC', 'electives'),
                },
            ],
            programOutcomes: [
                {
                    id: 'BSC-PLO1',
                    undergraduate: {
                        en: 'Demonstrate a broad foundational working knowledge of the Old and New Testaments.',
                        es: 'Demostrar un conocimiento operativo, amplio y fundamental del Antiguo y Nuevo Testamento.',
                    },
                    graduate: {
                        en: 'Discuss the significant historical, geographical, cultural, and theological background, as well as the purpose, theological themes, general outline, and interpretive challenges of each Bible book.',
                        es: 'Discutir el trasfondo histórico, geográfico, cultural y teológico, así como el propósito, los temas teológicos, el bosquejo general y los desafíos interpretativos de cada libro de la Biblia.',
                    },
                },
                {
                    id: 'BSC-PLO2',
                    undergraduate: {
                        en: 'Effectively use an observation, interpretation, & application approach to personal study of the Bible.',
                        es: 'Usar eficazmente un enfoque de observación, interpretación y aplicación en el estudio personal de la Biblia.',
                    },
                    graduate: {
                        en: 'Analyze and interpret various genre of biblical texts using historical-grammatical hermeneutical principles.',
                        es: 'Analizar e interpretar diversos géneros de textos bíblicos usando principios hermenéuticos histórico-gramaticales.',
                    },
                },
                {
                    id: 'BSC-PLO3',
                    undergraduate: {
                        en: 'Articulate with biblical support the major doctrines of the Christian church.',
                        es: 'Articular con apoyo bíblico las principales doctrinas de la iglesia cristiana.',
                    },
                    graduate: {
                        en: 'Articulate with biblical support the major doctrines of the Christian church and identify key differences with other major belief systems.',
                        es: 'Articular con apoyo bíblico las principales doctrinas de la iglesia cristiana e identificar diferencias clave con otros sistemas de creencias.',
                    },
                },
                {
                    id: 'BSC-PLO4',
                    undergraduate: {
                        en: 'Demonstrate application of biblical knowledge and principles in various contexts (e.g., the church, his/her own personal life, family, & society).',
                        es: 'Demostrar la aplicación del conocimiento y los principios bíblicos en diversos contextos (iglesia, vida personal, familia y sociedad).',
                    },
                    graduate: {
                        en: 'Demonstrate application of biblical knowledge and principles in various contexts (e.g., the church, his/her own personal life, family, & society).',
                        es: 'Demostrar la aplicación del conocimiento y los principios bíblicos en diversos contextos (iglesia, vida personal, familia y sociedad).',
                    },
                },
                {
                    id: 'BSC-PLO5',
                    undergraduate: {
                        en: 'Clearly articulate his/her testimony as well as the content of the gospel with clear biblical support in numerous settings and with various methods.',
                        es: 'Articular con claridad su testimonio y el contenido del evangelio, con apoyo bíblico claro, en diversos contextos y con distintos métodos.',
                    },
                    graduate: {
                        en: 'Clearly articulate his/her testimony as well as the content of the gospel with clear biblical support in numerous settings and with various methods.',
                        es: 'Articular con claridad su testimonio y el contenido del evangelio, con apoyo bíblico claro, en diversos contextos y con distintos métodos.',
                    },
                },
            ],
            criticalOutcome: {
                en: 'Enable each student to develop a consistent pattern of self-reflection and teachability leading to spiritual growth in his/her walk with the Lord (addressed through curricular and co-curricular means).',
                es: 'Habilitar en cada estudiante un patrón consistente de autorreflexión y disposición a ser enseñado que conduzca al crecimiento espiritual en su caminar con el Señor (abordado por medios curriculares y co-curriculares).',
            },
        },
        {
            id: 'dep',
            code: 'DEP',
            order: 2,
            name: {
                es: 'Diploma/Grado en Predicación Expositiva',
                en: 'Diploma/Degree in Expository Preaching',
            },
            prerequisiteCapId: 'bsc',
            isPrerequisiteFor: ['pmd'],
            levelOptions: ['undergraduate', 'graduate', 'mixed'],
            totalMinTcu: { freeStanding: 42, additionalIfStacked: 21 },
            awards: {
                undergraduate: {
                    es: 'Diploma en Predicación Expositiva',
                    en: 'Diploma in Expository Preaching',
                },
                graduate: {
                    es: 'Maestría en Artes en Predicación Expositiva',
                    en: 'Master of Arts in Expository Preaching',
                },
            },
            entranceRequirements: {
                undergraduate: 'Diploma de secundaria (según contexto local).',
                graduate: 'Grado de bachillerato (según contexto local); el TC debe certificar su cumplimiento.',
            },
            instructionalLanguageRule: 'Si los cursos no se dictan en la lengua materna del estudiante, la propuesta debe describir cómo se evalúa la fluidez en el idioma de instrucción.',
            facultyRequirements: {
                undergraduate: 'Al menos maestría relacionada con estudios bíblicos (MA en Estudios Bíblicos [AT, NT], Teología Sistemática o Teología Histórica). No califican MRE, MA en humanidades, historia o ciencias sociales.',
                graduate: 'Al menos M.Div.',
            },
            timeToCompleteMonthsTarget: 36,
            deliveryFormats: [
                'residential',
                'modular_onsite',
                'modular_blended',
                'online_independent',
                'online_with_sessions',
            ],
            subjectAreaRequirements: [
                {
                    code: 'OT_SURVEY',
                    name: { es: 'Panorama del Antiguo Testamento', en: 'Old Testament Survey' },
                    minTcu: 6,
                    fulfillableByPriorCap: true,
                    additionalBeyondPriorCap: 0,
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'NT_SURVEY',
                    name: { es: 'Panorama del Nuevo Testamento', en: 'New Testament Survey' },
                    minTcu: 3,
                    fulfillableByPriorCap: true,
                    additionalBeyondPriorCap: 0,
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'HERMENEUTICS',
                    name: { es: 'Hermenéutica', en: 'Hermeneutics' },
                    minTcu: 6,
                    fulfillableByPriorCap: true,
                    additionalBeyondPriorCap: 3,
                    note: '3 TCU pueden venir del BSC (*) + 3 TCU adicionales exclusivos del DEP (**).',
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'THEOLOGY',
                    name: { es: 'Teología Cristiana', en: 'Christian Theology' },
                    minTcu: 9,
                    fulfillableByPriorCap: true,
                    additionalBeyondPriorCap: 3,
                    note: '6 TCU pueden venir del BSC (*) + 3 TCU adicionales exclusivos del DEP (**).',
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'TEXTUAL_EXEGESIS',
                    name: { es: 'Análisis textual / Exégesis', en: 'Textual Analysis / Exegesis' },
                    minTcu: 3,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'EXPOSITORY_PREACHING',
                    name: {
                        es: 'Predicación expositiva (general y/o por género)',
                        en: 'Expository Preaching (general and/or specific genre)',
                    },
                    minTcu: 9,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'PASTORAL_MINISTRIES',
                    name: {
                        es: 'Ministerios pastorales (curso relacionado)',
                        en: 'Pastoral Ministries related course',
                    },
                    minTcu: 3,
                    fulfillableByPriorCap: false,
                    sourceRef: specRef('DEP', 'coreCoursework', 'Figure 3'),
                },
                {
                    code: 'ELECTIVES',
                    name: { es: 'Electivos', en: 'Electives' },
                    minTcu: 3,
                    fulfillableByPriorCap: true,
                    additionalBeyondPriorCap: 0,
                    sourceRef: specRef('DEP', 'electives'),
                },
            ],
            programOutcomes: [
                {
                    id: 'DEP-PLO1',
                    expandsBscArea: true,
                    undergraduate: {
                        en: 'Demonstrate a broad foundational working knowledge of the Old and New Testaments.',
                        es: 'Demostrar un conocimiento operativo, amplio y fundamental del Antiguo y Nuevo Testamento.',
                    },
                    graduate: {
                        en: 'Discuss the significant historical, geographical, cultural, and theological background, as well as the purpose, theological themes, general outline, and interpretive challenges of each Bible book.',
                        es: 'Discutir el trasfondo histórico, geográfico, cultural y teológico, así como el propósito, los temas teológicos, el bosquejo general y los desafíos interpretativos de cada libro de la Biblia.',
                    },
                },
                {
                    id: 'DEP-PLO2',
                    expandsBscArea: true,
                    undergraduate: {
                        en: 'Articulate with biblical support the major doctrines of the Christian church.',
                        es: 'Articular con apoyo bíblico las principales doctrinas de la iglesia cristiana.',
                    },
                    graduate: {
                        en: 'Articulate with biblical support the major doctrines of the Christian church and identify key differences with other major belief systems.',
                        es: 'Articular con apoyo bíblico las principales doctrinas de la iglesia cristiana e identificar diferencias clave con otros sistemas de creencias.',
                    },
                },
                {
                    id: 'DEP-PLO3',
                    expandsBscArea: true,
                    undergraduate: {
                        en: 'Demonstrate application of biblical knowledge and principles in various contexts (e.g., the church, his/her own personal life, family, & society).',
                        es: 'Demostrar la aplicación del conocimiento y los principios bíblicos en diversos contextos (iglesia, vida personal, familia y sociedad).',
                    },
                    graduate: {
                        en: 'Demonstrate application of biblical knowledge and principles in various contexts (e.g., the church, his/her own personal life, family, & society).',
                        es: 'Demostrar la aplicación del conocimiento y los principios bíblicos en diversos contextos (iglesia, vida personal, familia y sociedad).',
                    },
                },
                {
                    id: 'DEP-PLO4',
                    undergraduate: {
                        en: 'Analyze and interpret Old and New Testament genre using the historical grammatical hermeneutical method.',
                        es: 'Analizar e interpretar los géneros del Antiguo y Nuevo Testamento usando el método hermenéutico histórico-gramatical.',
                    },
                    graduate: {
                        en: 'Analyze and solve interpretive problems of Old and New Testament genre using the historical grammatical hermeneutical method.',
                        es: 'Analizar y resolver problemas interpretativos de los géneros del Antiguo y Nuevo Testamento usando el método hermenéutico histórico-gramatical.',
                    },
                },
                {
                    id: 'DEP-PLO5',
                    undergraduate: {
                        en: 'Prepare and deliver expository sermons in the most common biblical genres.',
                        es: 'Preparar y predicar sermones expositivos en los géneros bíblicos más comunes.',
                    },
                    graduate: {
                        en: 'Prepare and deliver expository sermons from a broad spectrum of biblical genres.',
                        es: 'Preparar y predicar sermones expositivos a partir de un amplio espectro de géneros bíblicos.',
                    },
                },
                {
                    id: 'DEP-PLO6',
                    undergraduate: {
                        en: 'Demonstrate the ability to effectively shepherd individual believers.',
                        es: 'Demostrar la capacidad de pastorear eficazmente a creyentes de forma individual.',
                    },
                    graduate: {
                        en: 'Demonstrate the ability to effectively shepherd individual believers.',
                        es: 'Demostrar la capacidad de pastorear eficazmente a creyentes de forma individual.',
                    },
                },
            ],
            criticalOutcome: {
                en: 'Enable each student to develop a consistent pattern of self-reflection and teachability leading to spiritual growth in his walk with the Lord (addressed through curricular and co-curricular means).',
                es: 'Habilitar en cada estudiante un patrón consistente de autorreflexión y disposición a ser enseñado que conduzca al crecimiento espiritual en su caminar con el Señor (abordado por medios curriculares y co-curriculares).',
            },
        },
    ],
};
/** Looks up a CAP definition by its public code. Throws on unknown codes so
 *  callers never audit against a silently-missing standard. */
export function getCapDefinition(framework, code) {
    const cap = framework.caps.find((c) => c.code === code);
    if (!cap)
        throw new Error(`CAP definition not found in framework: ${code}`);
    return cap;
}
//# sourceMappingURL=cap-tmai-2022-03.js.map